import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Sparkles, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && (user.role === "owner" || user.role === "developer")) {
    return <Navigate to={user.role === "developer" ? "/admin/dev" : "/admin"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      toast.success("Signed in");
      const role = (res && res.role) || (typeof window !== "undefined" && JSON.parse(localStorage.getItem("pa_admin_user") || "null")?.role);
      // AuthContext sets user via state; rely on the redirect-on-render above by triggering a navigate.
      navigate(role === "developer" ? "/admin/dev" : "/admin", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-100 shadow-card p-8">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#8B5CF6] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </span>
          <span className="font-display font-bold text-[#0F172A]">P &amp; A Cleaning</span>
        </Link>
        <h1 className="text-2xl font-display font-bold text-[#0F172A]">Admin sign in</h1>
        <p className="mt-1 text-sm text-[#475569]">Manage estimate requests, services, pricing & content.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-login-email"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors disabled:opacity-50"
          >
            <Lock className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
